/**
 * Web-compatible wrapper for react-native-animatable
 * Simply renders Views without animations to avoid React 19 findDOMNode issues
 * 
 * For web, we skip animations entirely - they're not critical for functionality
 */

import React from 'react';
import { View, Text, Image } from 'react-native';

// Just pass through as regular View - no animations needed on web
const AnimatableView = React.forwardRef<any, any>((props, ref) => {
  const {
    animation,
    duration,
    delay,
    direction,
    easing,
    iterationCount,
    transition,
    onAnimationBegin,
    onAnimationEnd,
    useNativeDriver,
    children,
    ...viewProps
  } = props;

  // Call animation callbacks if provided (for compatibility)
  React.useEffect(() => {
    if (animation && onAnimationBegin) {
      onAnimationBegin();
    }
    if (animation && onAnimationEnd) {
      // Call after a short delay to simulate animation
      const timer = setTimeout(() => {
        onAnimationEnd();
      }, duration || 300);
      return () => clearTimeout(timer);
    }
  }, [animation, onAnimationBegin, onAnimationEnd, duration]);

  // Filter out text nodes and wrap them in Text component if needed
  const processedChildren = React.useMemo(() => {
    if (!children) return children;

    // If children is a string or number, wrap it in Text
    if (typeof children === 'string' || typeof children === 'number') {
      return <Text>{children}</Text>;
    }

    // If children is an array, process each child
    if (Array.isArray(children)) {
      return children.map((child, index) => {
        // Skip null, undefined, boolean, and empty strings
        if (child == null || typeof child === 'boolean' || child === '') {
          return null;
        }
        // Wrap string or number children in Text
        if (typeof child === 'string' || typeof child === 'number') {
          // Check if it's just whitespace
          if (typeof child === 'string' && child.trim() === '') {
            return null;
          }
          return <Text key={index}>{child}</Text>;
        }
        return child;
      }).filter(Boolean);
    }

    return children;
  }, [children]);

  return <View ref={ref} {...viewProps}>{processedChildren}</View>;
});

AnimatableView.displayName = 'AnimatableView';

// Text component - just pass through
const AnimatableText = React.forwardRef<any, any>((props, ref) => {
  const {
    animation,
    duration,
    delay,
    direction,
    easing,
    iterationCount,
    transition,
    onAnimationBegin,
    onAnimationEnd,
    useNativeDriver,
    ...textProps
  } = props;

  return <Text ref={ref} {...textProps} />;
});

AnimatableText.displayName = 'AnimatableText';

// Image component - just pass through  
const AnimatableImage = React.forwardRef<any, any>((props, ref) => {
  const {
    animation,
    duration,
    delay,
    direction,
    easing,
    iterationCount,
    transition,
    onAnimationBegin,
    onAnimationEnd,
    useNativeDriver,
    ...imageProps
  } = props;

  return <Image ref={ref} {...imageProps} />;
});

AnimatableImage.displayName = 'AnimatableImage';

// Create animation (no-op on web, just returns the component)
const createAnimatableComponent = (component: any) => {
  return component;
};

// Export with the same API as react-native-animatable
export {
  AnimatableView as View,
  AnimatableText as Text,
  AnimatableImage as Image,
  createAnimatableComponent,
};

export default {
  View: AnimatableView,
  Text: AnimatableText,
  Image: AnimatableImage,
  createAnimatableComponent,
};
